import React, { useRef, useEffect } from "react";
import { Box } from "@mui/material";

/**
 * Numeric one-time-code input rendered as individual digit boxes.
 *
 * Controlled: `value` is the digits typed so far ("", "1", ..., "12345");
 * `onChange(next)` receives the cleaned string and `onComplete(code)` fires
 * once when the last box fills (parent decides whether to auto-submit).
 *
 * Keyboard contract: digits auto-advance, Backspace clears/walks back,
 * arrows navigate, paste distributes a full code across the boxes
 * (autocomplete="one-time-code" lets iOS/macOS suggest the emailed code).
 */
const OtpInput = ({
  length = 5,
  value = "",
  onChange,
  onComplete,
  disabled = false,
  error = false,
  ariaLabel = "Verification code",
  autoFocus = true,
}) => {
  const inputsRef = useRef([]);
  const completedRef = useRef(false);

  const digits = Array.from({ length }, (_, i) => value[i] || "");

  useEffect(() => {
    if (autoFocus && !disabled) inputsRef.current[0]?.focus();
  }, [autoFocus, disabled]);

  // onComplete must fire once per fill, not on every render at full length.
  useEffect(() => {
    if (value.length === length && !completedRef.current) {
      completedRef.current = true;
      onComplete?.(value);
    }
    if (value.length < length) completedRef.current = false;
  }, [value, length, onComplete]);

  const focusBox = (index) => {
    const el = inputsRef.current[Math.max(0, Math.min(index, length - 1))];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const distributeFrom = (startIndex, incoming) => {
    const clean = incoming.replace(/\D/g, "");
    if (!clean) return;
    // A full-length paste replaces everything regardless of which box
    // received it - people copy the whole code from the email.
    const start = clean.length >= length ? 0 : startIndex;
    const next = (
      value.slice(0, start) +
      clean +
      value.slice(start + clean.length)
    ).slice(0, length);
    onChange(next);
    focusBox(start + clean.length);
  };

  const handleChange = (index, e) => {
    distributeFrom(index, e.target.value);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1));
        focusBox(index);
      } else if (index > 0) {
        onChange(value.slice(0, index - 1) + value.slice(index));
        focusBox(index - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (index, e) => {
    e.preventDefault();
    distributeFrom(index, e.clipboardData.getData("text"));
  };

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{ display: "flex", gap: 1.25, justifyContent: "center" }}
    >
      {digits.map((digit, i) => (
        <Box
          key={i}
          component="input"
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={length}
          value={digit}
          disabled={disabled}
          aria-label={`${ariaLabel} ${i + 1} / ${length}`}
          aria-invalid={error || undefined}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => e.target.select()}
          sx={{
            width: "clamp(44px, 12vw, 56px)",
            height: "clamp(56px, 15vw, 68px)",
            textAlign: "center",
            fontSize: "1.5rem",
            fontWeight: 600,
            fontFamily: "inherit",
            color: "rgba(255,255,255,0.92)",
            backgroundColor: "rgba(255,255,255,0.06)",
            border: "1px solid",
            borderColor: error
              ? "rgba(229,115,115,0.7)"
              : "rgba(255,255,255,0.14)",
            borderRadius: "12px",
            outline: "none",
            caretColor: "#DAA520",
            transition: "border-color 120ms ease, background-color 120ms ease",
            "&:focus": {
              borderColor: "#DAA520",
              backgroundColor: "rgba(255,255,255,0.08)",
            },
            "&:disabled": {
              opacity: 0.5,
              cursor: "not-allowed",
            },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpInput;
